// Ian - 2026-08-16 - Typeable quantity inputs.
//
// NOT part of the New PDP trial. This fixes a problem on the CURRENT live template too: the
// quantity input was readonly everywhere, so a customer ordering 40 metres had to click the
// plus button eighty times. Kept in its own file and its own commit so it can be reverted
// without touching the trial, and so the trial can be rolled back without reverting this.
//
// The dangerous part, and the reason this is careful rather than clever:
//
// There are TWO quantities in play on a by-the-metre product. The visible input is
// `quantity_per_metre` and holds METRES. A hidden sibling named `quantity` holds INVENTORY
// UNITS, and 1 metre = 2 units. The theme keeps them in step in main.js, but only inside
// the plus/minus click handler and the Enter keydown handler:
//
//     hiddenInput.value = (1 / this.input.step) * this.input.value;
//
// Nothing runs that on `change` or `blur`. So simply removing readonly would let a customer
// type 40, blur the field, and have the cart take whatever the hidden field last held. On a
// product that gets physically cut to length that is a silent mis-order, and worse than
// eighty clicks. This script fires the same conversion, read from the theme's own code
// rather than reimplemented from the spec.

(() => {
  const SELECTOR = '.qty-input__input:not([readonly])';

  const sync = (input) => {
    const step = parseFloat(input.step) || 1;
    const min = parseFloat(input.min);
    const max = parseFloat(input.max);
    const raw = parseFloat(input.value);

    // Empty or non-numeric falls back to min, never to zero and never to NaN.
    let value = Number.isFinite(raw) ? raw : (Number.isFinite(min) ? min : step);

    // Snap to the nearest valid step, then clamp. Order matters: snapping a value that is
    // already at max must not push it past max.
    const base = Number.isFinite(min) ? min : 0;
    value = base + Math.round((value - base) / step) * step;
    if (Number.isFinite(min) && value < min) value = min;
    if (Number.isFinite(max) && value > max) value = max;

    // Kill floating point dust from the step arithmetic (0.30000000000000004).
    value = Math.round(value * 1000) / 1000;

    const changed = String(value) !== input.value;
    if (changed) input.value = value;

    // The metre-to-unit conversion, matching main.js exactly.
    if (input.name === 'quantity_per_metre') {
      const hidden = input.parentElement && input.parentElement.querySelector('input[name="quantity"]');
      if (hidden) {
        hidden.value = (1 / step) * value;
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    return changed;
  };

  const handle = (evt) => {
    const input = evt.target.closest ? evt.target.closest(SELECTOR) : null;
    if (!input || input.dataset.ianSyncing === '1') return;

    input.dataset.ianSyncing = '1';
    const changed = sync(input);
    // Re-announce only when the value was actually corrected, so the theme's own listeners
    // see a normal change event without this handler re-entering itself.
    if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
    delete input.dataset.ianSyncing;
  };

  // change and blur, never per keystroke: correcting mid-typing fights the customer.
  document.addEventListener('change', handle, true);
  document.addEventListener('blur', handle, true);
})();
